
// src/services/api/settingsService.js
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Settings API Service
//  Provides methods to fetch, update, backup, restore, and manage platform settings
// ─────────────────────────────────────────────────────────────────────────────

import { db, auth } from '../../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, addDoc, deleteDoc, deleteField, writeBatch, setDoc, query, where } from 'firebase/firestore';
import { uploadAdminFile } from '../firebase/storage';

const SETTINGS_COLLECTION = 'systemConfig';
const SETTINGS_DOC_ID = 'platform';

const GATEWAY_SECRET_KEYS = new Set(['apiSecret', 'webhookSecret', 'secretKey', 'privateKey', 'apiToken', 'token', 'password']);
const sanitizeGatewayRecord = (data = {}) => {
    const safe = {};
    Object.entries(data || {}).forEach(([key, value]) => {
        if (!GATEWAY_SECRET_KEYS.has(key)) safe[key] = value;
    });
    // `apiKey` was ambiguous in older builds and was rendered as a password.
    // Do not keep accepting it as a browser-stored credential. A clearly named
    // public/publishable key is safe to retain.
    delete safe.apiKey;
    return safe;
};
const gatewaySecretDeletionPatch = () => ({
    apiKey: deleteField(), apiSecret: deleteField(), webhookSecret: deleteField(),
    secretKey: deleteField(), privateKey: deleteField(), apiToken: deleteField(), token: deleteField(), password: deleteField(),
});

const normalizeAdminRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
const requireSuperAdminSettingsAccess = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Super Admin authentication is required.');
    const snap = await getDoc(doc(db, 'users', uid));
    const data = snap.exists() ? snap.data() || {} : {};
    if (!['super_admin', 'superadmin'].includes(normalizeAdminRole(data.role || data.userRole)) || data.isActive === false || data.isBlocked === true) {
        throw new Error('Only Super Admin can manage platform administrators.');
    }
    return uid;
};

class SettingsService {
    /**
     * Get default settings structure
     */
    getDefaultSettings() {
        return {
            value: {
                general: {
                    platformName: 'TELCOFAIR',
                    logo: '',
                    currency: 'EUR',
                    favicon: '',
                    maintenanceMode: false,
                    defaultLanguage: 'en',
                    timezone: 'UTC',
                    dateFormat: 'MM/DD/YYYY',
                },
                security: {
                    twoFactorRequired: false,
                    sessionTimeout: 60,
                    maxLoginAttempts: 5,
                    lockoutDuration: 30,
                    passwordMinLength: 8,
                    requireUppercase: true,
                    requireLowercase: true,
                    requireNumbers: true,
                    requireSpecialChars: true,
                },
                email: {
                    smtpHost: '',
                    smtpPort: 587,
                    smtpSecure: true,
                    smtpUser: '',
                    smtpPass: '',
                    fromEmail: '',
                    fromName: '',
                },
                payment: {
                    provider: 'revolut',
                    taxRate: 0,
                    invoicePrefix: 'INV-',
                    enableRecurring: true,
                    enableRefunds: true,
                    minimumAmount: 1,
                },
                subscription: {
                    plan: 'pro',
                    currency: 'EUR',
                    status: 'active',
                    nextBillingDate: null,
                    autoRenew: true,
                },
                sms: {
                    provider: 'twilio',
                    defaultCountryCode: '+1',
                    enableDeliveryReports: true,
                    maxRetries: 3,
                    retryDelay: 60,
                },
                whatsapp: {
                    provider: 'twilio',
                    enableTemplates: true,
                    enableDeliveryReports: true,
                    maxRetries: 3,
                    retryDelay: 60,
                },
            },
            updatedAt: null,
            updatedBy: null,
        };
    }

    /**
     * Fetch all platform settings
     */
    async getSettings() {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                return docSnap.data();
            }
            
            // Initialize settings if they don't exist
            await this.initializeSettings();
            return this.getDefaultSettings();
        } catch (error) {
            console.error('Error fetching settings:', error);
            throw new Error('Failed to fetch settings');
        }
    }

    /**
     * Get specific section of settings
     */
    async getSettingsSection(section) {
        try {
            const settings = await this.getSettings();
            return settings.value?.[section] || {};
        } catch (error) {
            console.error(`Error fetching ${section} settings:`, error);
            throw error;
        }
    }

    /**
     * Initialize settings document if it doesn't exist
     */
    async initializeSettings() {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            const docSnap = await getDoc(docRef);
            
            if (!docSnap.exists()) {
                const defaults = this.getDefaultSettings();
                await setDoc(docRef, {
                    ...defaults,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    updatedBy: actorUid,
                });
                return { success: true, message: 'Settings initialized' };
            }
            return { success: true, message: 'Settings already exist' };
        } catch (error) {
            console.error('Error initializing settings:', error);
            throw new Error('Failed to initialize settings');
        }
    }

    /**
     * Update general settings
     */
    async updateGeneralSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            await setDoc(docRef, {
                value: { general: data },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });

            // Public authentication surfaces in both TELCOFAIR portals read the
            // safe branding mirror. Keep it synchronized with Admin Settings.
            const logo = data?.logoUrl || data?.logo || '';
            const platformName = data?.platformName || data?.appName || 'TELCOFAIR';
            const theme = {
                primary: /^#[0-9a-f]{6}$/i.test(String(data?.primaryColor || '')) ? String(data.primaryColor).toUpperCase() : '#2E6F8E',
                secondary: /^#[0-9a-f]{6}$/i.test(String(data?.secondaryColor || '')) ? String(data.secondaryColor).toUpperCase() : '#42B89B',
                accent: /^#[0-9a-f]{6}$/i.test(String(data?.accentColor || '')) ? String(data.accentColor).toUpperCase() : '#7C5CF2',
            };
            await Promise.all([
                setDoc(doc(db, 'systemSettings', 'app_settings'), {
                    appName: platformName, platformName, logo, logoUrl: logo,
                    updatedAt: serverTimestamp(), updatedBy: actorUid,
                }, { merge: true }),
                setDoc(doc(db, 'systemSettings', 'branding'), {
                    appName: platformName, companyName: platformName, logo, logoUrl: logo, companyLogo: logo,
                    updatedAt: serverTimestamp(), updatedBy: actorUid,
                }, { merge: true }),
                setDoc(doc(db, 'systemSettings', 'public_runtime'), {
                    branding: { appName: platformName, platformName, logo, logoUrl: logo },
                    theme,
                    updatedAt: serverTimestamp(), updatedBy: actorUid,
                }, { merge: true }),
            ]);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('telcofair:platform-branding-updated'));
                window.dispatchEvent(new CustomEvent('telcofair:platform-theme-updated', { detail: theme }));
            }
            return { success: true };
        } catch (error) {
            console.error('Error updating general settings:', error);
            throw new Error('Failed to update general settings');
        }
    }

    /**
     * Update security settings
     */
    async updateSecuritySettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            const policy = {
                minLength: Math.max(8, Math.min(128, Number(data?.passwordMinLength || 8))),
                requireUppercase: data?.requireUppercase !== false,
                requireLowercase: data?.requireLowercase !== false,
                requireNumbers: data?.requireNumbers !== false,
                requireSpecialChars: data?.requireSpecialChars !== false,
            };
            // MFA, authoritative lockout and idle-session enforcement require Firebase Auth/backend support.
            // Preserve the requested values for administration, but expose only the password policy to
            // the public registration runtime so the UI never pretends browser-only lockout is security.
            const safeSecurity = {
                ...data,
                passwordMinLength: policy.minLength,
                requireUppercase: policy.requireUppercase,
                requireLowercase: policy.requireLowercase,
                requireNumbers: policy.requireNumbers,
                requireSpecialChars: policy.requireSpecialChars,
            };
            await Promise.all([
                setDoc(docRef, {
                    value: { security: safeSecurity },
                    updatedAt: serverTimestamp(),
                    updatedBy: actorUid,
                }, { merge: true }),
                setDoc(doc(db, 'systemSettings', 'public_runtime'), {
                    security: { passwordPolicy: policy },
                    updatedAt: serverTimestamp(),
                    updatedBy: actorUid,
                }, { merge: true }),
            ]);
            return { success: true, passwordPolicy: policy };
        } catch (error) {
            console.error('Error updating security settings:', error);
            throw new Error('Failed to update security settings');
        }
    }

    /**
     * Update email settings
     */
    async updateEmailSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            const { smtpPass: _discardedSecret, ...safeEmail } = data || {};
            await setDoc(docRef, {
                value: { email: safeEmail },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });
            // Remove plaintext SMTP passwords stored by older browser-only builds.
            await updateDoc(docRef, { 'value.email.smtpPass': deleteField() }).catch(() => {});
            return { success: true };
        } catch (error) {
            console.error('Error updating email settings:', error);
            throw new Error('Failed to update email settings');
        }
    }

    /**
     * Update payment settings
     */
    async updatePaymentSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            await setDoc(docRef, {
                value: { payment: data },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error('Error updating payment settings:', error);
            throw new Error('Failed to update payment settings');
        }
    }

    /**
     * Update subscription settings
     */
    async updateSubscriptionSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            await setDoc(docRef, {
                value: { subscription: data },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error('Error updating subscription settings:', error);
            throw new Error('Failed to update subscription settings');
        }
    }

    /**
     * Update SMS settings
     */
    async updateSmsSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            await setDoc(docRef, {
                value: { sms: data },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error('Error updating SMS settings:', error);
            throw new Error('Failed to update SMS settings');
        }
    }

    /**
     * Update WhatsApp settings
     */
    async updateWhatsAppSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
            await setDoc(docRef, {
                value: { whatsapp: data },
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error('Error updating WhatsApp settings:', error);
            throw new Error('Failed to update WhatsApp settings');
        }
    }


    /**
     * Public runtime settings are safe browser-readable platform settings.
     * Firebase uploads are direct. Cloudinary keeps only safe provider metadata
     * here; upload signatures and secrets remain server-side.
     */
    async getPublicRuntime() {
        const snap = await getDoc(doc(db, 'systemSettings', 'public_runtime'));
        return snap.exists() ? snap.data() || {} : {};
    }

    async updateStorageSettings(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        userId = actorUid;
        const provider = ['firebase', 'cloudinary'].includes(String(data?.provider || '').toLowerCase())
            ? String(data.provider).toLowerCase() : 'firebase';
        const safe = {
            provider,
            firebase: { basePath: 'companies' },
            cloudinary: {
                cloudName: String(data?.cloudinary?.cloudName || '').trim(),
                uploadPreset: String(data?.cloudinary?.uploadPreset || '').trim(),
                folder: String(data?.cloudinary?.folder || 'telcofair').trim().replace(/^\/+|\/+$/g, '') || 'telcofair',
            },
        };
        if (provider === 'cloudinary' && (!safe.cloudinary.cloudName || !safe.cloudinary.uploadPreset)) {
            throw new Error('Cloudinary requires a Cloud Name and unsigned Upload Preset.');
        }
        await Promise.all([
            setDoc(doc(db, 'systemSettings', 'public_runtime'), { storage: safe, updatedAt: serverTimestamp(), updatedBy: userId || 'admin' }, { merge: true }),
            // Keep the secure Settings loader in sync without replacing the rest
            // of the admin_runtime.settings map.
            updateDoc(doc(db, 'systemSettings', 'admin_runtime'), {
                'settings.storage': safe,
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            }).catch(async () => {
                await setDoc(doc(db, 'systemSettings', 'admin_runtime'), {
                    settings: { storage: safe },
                    updatedAt: serverTimestamp(),
                    updatedBy: actorUid,
                }, { merge: true });
            }),
            setDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID), { storage: safe, updatedAt: serverTimestamp(), updatedBy: userId || 'admin' }, { merge: true }),
        ]);
        return safe;
    }

    /**
     * Save browser-safe platform service settings directly to Firestore.
     * Secrets are deliberately excluded and remain server-managed. This keeps
     * normal Admin settings usable even when callable Functions are temporarily
     * unavailable.
     */
    async updatePlatformRuntimeServices(data, userId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        userId = actorUid;

        const storage = {
            provider: ['firebase', 'cloudinary'].includes(String(data?.storage?.provider || '').toLowerCase()) ? String(data.storage.provider).toLowerCase() : 'firebase',
            firebase: { basePath: 'companies' },
            cloudinary: {
                cloudName: String(data?.storage?.cloudinary?.cloudName || '').trim(),
                uploadPreset: String(data?.storage?.cloudinary?.uploadPreset || '').trim(),
                folder: String(data?.storage?.cloudinary?.folder || 'telcofair').trim().replace(/^\/+|\/+$/g, '') || 'telcofair',
            },
        };
        if (storage.provider === 'cloudinary' && (!storage.cloudinary.cloudName || !storage.cloudinary.uploadPreset)) {
            throw new Error('Cloudinary requires a Cloud Name and unsigned Upload Preset.');
        }
        const safeSettings = {
            storage,
            payments: {
                enabled: Boolean(data?.payments?.enabled),
                provider: ['stripe','revolut'].includes(data?.payments?.provider) ? data.payments.provider : 'revolut',
                currency: data?.payments?.currency || 'EUR',
                online: { enabled: data?.payments?.online?.enabled !== false },
                stripe: { publishableKey: data?.payments?.stripe?.publishableKey || '' },
                revolut: { publicKey: data?.payments?.revolut?.publicKey || '', environment: data?.payments?.revolut?.environment || 'sandbox' },
                manual: {
                    approvalRequired: data?.payments?.manual?.approvalRequired !== false,
                    upi: {
                        enabled: Boolean(data?.payments?.manual?.upi?.enabled), upiId: data?.payments?.manual?.upi?.upiId || '',
                        payeeName: data?.payments?.manual?.upi?.payeeName || '', qrUrl: data?.payments?.manual?.upi?.qrUrl || '',
                        instructions: data?.payments?.manual?.upi?.instructions || '',
                    },
                    bankTransfer: {
                        enabled: Boolean(data?.payments?.manual?.bankTransfer?.enabled), accountHolder: data?.payments?.manual?.bankTransfer?.accountHolder || '',
                        bankName: data?.payments?.manual?.bankTransfer?.bankName || '', accountNumber: data?.payments?.manual?.bankTransfer?.accountNumber || '',
                        ifsc: data?.payments?.manual?.bankTransfer?.ifsc || '', branch: data?.payments?.manual?.bankTransfer?.branch || '',
                        swift: data?.payments?.manual?.bankTransfer?.swift || '', iban: data?.payments?.manual?.bankTransfer?.iban || '',
                        instructions: data?.payments?.manual?.bankTransfer?.instructions || '',
                    },
                },
                receipt: {
                    enabled: data?.payments?.receipt?.enabled !== false, title: data?.payments?.receipt?.title || 'TELCOFAIR PAYMENT RECEIPT',
                    prefix: data?.payments?.receipt?.prefix || 'TF-RCPT-', accentColor: data?.payments?.receipt?.accentColor || '#2E6F8E',
                    logoUrl: data?.payments?.receipt?.logoUrl || '', footer: data?.payments?.receipt?.footer || 'Thank you for your payment.',
                    terms: data?.payments?.receipt?.terms || 'This receipt confirms a payment recorded and approved by TELCOFAIR.',
                    showCompanyLogo: data?.payments?.receipt?.showCompanyLogo !== false, showPaymentMethod: data?.payments?.receipt?.showPaymentMethod !== false,
                    showPackage: data?.payments?.receipt?.showPackage !== false, emailOnApproval: data?.payments?.receipt?.emailOnApproval !== false,
                },
            },
            email: {
                enabled: Boolean(data?.email?.enabled), provider: data?.email?.provider || 'smtp',
                fromEmail: data?.email?.fromEmail || '', fromName: data?.email?.fromName || 'TELCOFAIR', replyTo: data?.email?.replyTo || '',
                smtp: { host: data?.email?.smtp?.host || '', port: Number(data?.email?.smtp?.port || 587), secure: Boolean(data?.email?.smtp?.secure), user: data?.email?.smtp?.user || '' },
            },
            sms: {
                enabled: Boolean(data?.sms?.enabled), provider: data?.sms?.provider || 'twilio',
                twilio: { accountSid: data?.sms?.twilio?.accountSid || '', fromNumber: data?.sms?.twilio?.fromNumber || '', messagingServiceSid: data?.sms?.twilio?.messagingServiceSid || '' },
                msg91: { templateId: data?.sms?.msg91?.templateId || '', senderId: data?.sms?.msg91?.senderId || '', route: data?.sms?.msg91?.route || '4', country: data?.sms?.msg91?.country || '91', endpoint: data?.sms?.msg91?.endpoint || '' },
            },
            push: { enabled: Boolean(data?.push?.enabled), webVapidKey: data?.push?.webVapidKey || '' },
            registrationReminders: {
                enabled: data?.registrationReminders?.enabled !== false,
                firstReminderMinutes: Number(data?.registrationReminders?.firstReminderMinutes || 60),
                repeatHours: Number(data?.registrationReminders?.repeatHours || 48),
                maxReminders: Number(data?.registrationReminders?.maxReminders || 8),
                expiryDays: Number(data?.registrationReminders?.expiryDays || 30),
                verificationReminderEnabled: data?.registrationReminders?.verificationReminderEnabled !== false,
                profileReminderEnabled: data?.registrationReminders?.profileReminderEnabled !== false,
                paymentReminderEnabled: data?.registrationReminders?.paymentReminderEnabled !== false,
            },
            ai: {
                enabled: Boolean(data?.ai?.enabled), provider: data?.ai?.provider || 'openai', model: data?.ai?.model || 'gpt-4o-mini',
                maxOutputTokens: Number(data?.ai?.maxOutputTokens || 700), autoGenerateIntro: data?.ai?.autoGenerateIntro !== false,
                autoGenerateMessages: Boolean(data?.ai?.autoGenerateMessages), autoRefreshMatches: data?.ai?.autoRefreshMatches !== false,
                systemInstructions: data?.ai?.systemInstructions || '',
            },
            agora: { enabled: Boolean(data?.agora?.enabled), appId: data?.agora?.appId || '', tokenTtlSeconds: Number(data?.agora?.tokenTtlSeconds || 3600) },
            calendar: {
                enabled: Boolean(data?.calendar?.enabled), icsFeedEnabled: data?.calendar?.icsFeedEnabled !== false,
                google: { enabled: Boolean(data?.calendar?.google?.enabled), clientId: data?.calendar?.google?.clientId || '' },
                microsoft: { enabled: Boolean(data?.calendar?.microsoft?.enabled), clientId: data?.calendar?.microsoft?.clientId || '', tenant: data?.calendar?.microsoft?.tenant || 'common' },
            },
        };
        const publicRuntime = {
            storage,
            features: {
                ai: safeSettings.ai.enabled, videoMeetings: safeSettings.agora.enabled, externalCalendars: safeSettings.calendar.enabled,
                pushNotifications: safeSettings.push.enabled, payments: safeSettings.payments.enabled,
            },
            payments: {
                enabled: safeSettings.payments.enabled, provider: safeSettings.payments.provider, currency: safeSettings.payments.currency, online: safeSettings.payments.online || { enabled: true },
                stripePublishableKey: safeSettings.payments.provider === 'stripe' ? safeSettings.payments.stripe.publishableKey : '',
                revolutPublicKey: safeSettings.payments.provider === 'revolut' ? safeSettings.payments.revolut.publicKey : '',
                manual: safeSettings.payments.manual || {},
                receipt: safeSettings.payments.receipt || {},
            },
            meeting: { agoraEnabled: safeSettings.agora.enabled, agoraAppId: safeSettings.agora.appId, calendarEnabled: safeSettings.calendar.enabled },
            notifications: { emailEnabled: safeSettings.email.enabled, pushEnabled: safeSettings.push.enabled },
        };
        await Promise.all([
            setDoc(doc(db, 'systemSettings', 'admin_runtime'), { settings: safeSettings, updatedAt: serverTimestamp(), updatedBy: userId }, { merge: true }),
            setDoc(doc(db, 'systemSettings', 'public_runtime'), { ...publicRuntime, updatedAt: serverTimestamp(), updatedBy: userId }, { merge: true }),
            setDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID), {
                storage, featureFlags: publicRuntime.features,
                registrationIntegrations: {
                    paymentEnabled: safeSettings.payments.enabled, paymentProvider: safeSettings.payments.provider,
                    emailEnabled: safeSettings.email.enabled, emailProvider: safeSettings.email.provider,
                    smsEnabled: safeSettings.sms.enabled, smsProvider: safeSettings.sms.provider,
                    pushEnabled: safeSettings.push.enabled, webVapidKey: safeSettings.push.webVapidKey,
                    remindersEnabled: safeSettings.registrationReminders.enabled, reminderFirstMinutes: safeSettings.registrationReminders.firstReminderMinutes,
                    reminderRepeatHours: safeSettings.registrationReminders.repeatHours, reminderMaxCount: safeSettings.registrationReminders.maxReminders,
                    reminderExpiryDays: safeSettings.registrationReminders.expiryDays, calendarEnabled: safeSettings.calendar.enabled,
                    googleCalendarEnabled: safeSettings.calendar.google.enabled, microsoftCalendarEnabled: safeSettings.calendar.microsoft.enabled,
                    icsFeedEnabled: safeSettings.calendar.icsFeedEnabled,
                },
                updatedAt: serverTimestamp(), updatedBy: actorUid,
            }, { merge: true }),
        ]);
        return { safeSettings, publicRuntime };
    }

    /**
     * Upload platform logo
     */
    async uploadLogo(file) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const asset = await uploadAdminFile({ file, scope: 'platform', folder: 'branding/platform-logo', kind: 'image' });
            return asset.url;
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw new Error(error?.message || 'Failed to upload logo');
        }
    }

    /**
     * Get all data from a collection (for backup)
     */
    async getCollectionData(collectionName) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const snapshot = await getDocs(collection(db, collectionName));
            const data = [];
            snapshot.forEach((doc) => {
                data.push({ id: doc.id, ...doc.data() });
            });
            return data;
        } catch (error) {
            console.error(`Error fetching collection ${collectionName}:`, error);
            throw new Error(`Failed to fetch collection ${collectionName}`);
        }
    }

    /**
     * Restore data to a collection (for restore)
     */
    async restoreCollection(collectionName, docs) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            if (!Array.isArray(docs)) throw new Error('Backup data must be an array.');
            let batch = writeBatch(db);
            let count = 0;
            let restored = 0;

            for (const item of docs) {
                if (!item?.id) continue;
                const { id, ...data } = item;
                const docRef = doc(db, collectionName, id);
                batch.set(docRef, data, { merge: true });
                count++;
                restored++;

                // Keep headroom under Firestore's 500-operation batch limit and
                // create a fresh batch after every commit. A committed batch
                // cannot be reused.
                if (count >= 450) {
                    await batch.commit();
                    batch = writeBatch(db);
                    count = 0;
                }
            }
            if (count > 0) await batch.commit();
            return { success: true, restored };
        } catch (error) {
            console.error(`Error restoring collection ${collectionName}:`, error);
            throw new Error(error?.message || `Failed to restore collection ${collectionName}`);
        }
    }

    /**
     * Wipe all data from a collection
     */
    async wipeCollection(collectionName) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const snapshot = await getDocs(collection(db, collectionName));
            let batch = writeBatch(db);
            let count = 0;
            let deleted = 0;

            for (const docSnap of snapshot.docs) {
                batch.delete(docSnap.ref);
                count++;
                deleted++;
                if (count >= 450) {
                    await batch.commit();
                    batch = writeBatch(db);
                    count = 0;
                }
            }

            if (count > 0) await batch.commit();
            return { success: true, deleted };
        } catch (error) {
            console.error(`Error wiping collection ${collectionName}:`, error);
            throw new Error(error?.message || `Failed to wipe collection ${collectionName}`);
        }
    }

    /**
     * Get all payment gateways
     */
    async getPaymentGateways(companyId = 'system') {
        const actorUid = await requireSuperAdminSettingsAccess();
        companyId = 'system';
        try {
            const q = query(
                collection(db, 'paymentGateways'),
                where('companyId', '==', companyId)
            );
            const snapshot = await getDocs(q);
            const gateways = [];
            const cleanup = [];
            snapshot.forEach((row) => {
                const raw = row.data() || {};
                if ([...GATEWAY_SECRET_KEYS, 'apiKey'].some((key) => Object.prototype.hasOwnProperty.call(raw, key))) {
                    cleanup.push(updateDoc(row.ref, { ...gatewaySecretDeletionPatch(), updatedAt: serverTimestamp(), updatedBy: actorUid }));
                }
                gateways.push({ id: row.id, ...sanitizeGatewayRecord(raw) });
            });
            if (cleanup.length) await Promise.allSettled(cleanup);
            return gateways;
        } catch (error) {
            console.error('Error fetching payment gateways:', error);
            throw new Error('Failed to fetch payment gateways');
        }
    }

    /**
     * Add a payment gateway
     */
    async addPaymentGateway(data, companyId = 'system') {
        const actorUid = await requireSuperAdminSettingsAccess();
        companyId = 'system';
        try {
            const gatewayData = {
                ...sanitizeGatewayRecord(data),
                companyId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const docRef = await addDoc(collection(db, 'paymentGateways'), gatewayData);
            return { id: docRef.id, ...gatewayData };
        } catch (error) {
            console.error('Error adding payment gateway:', error);
            throw new Error('Failed to add payment gateway');
        }
    }

    /**
     * Update a payment gateway
     */
    async updatePaymentGateway(gatewayId, data) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, 'paymentGateways', gatewayId);
            await updateDoc(docRef, {
                ...sanitizeGatewayRecord(data),
                ...gatewaySecretDeletionPatch(),
                updatedAt: serverTimestamp(),
                updatedBy: actorUid,
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating payment gateway:', error);
            throw new Error('Failed to update payment gateway');
        }
    }

    /**
     * Delete a payment gateway
     */
    async deletePaymentGateway(gatewayId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            await deleteDoc(doc(db, 'paymentGateways', gatewayId));
            return { success: true };
        } catch (error) {
            console.error('Error deleting payment gateway:', error);
            throw new Error('Failed to delete payment gateway');
        }
    }

    /**
     * Get all packages
     */
    async getPackages(companyId = 'system') {
        const actorUid = await requireSuperAdminSettingsAccess();
        companyId = 'system';
        try {
            const q = query(
                collection(db, 'packages'),
                where('companyId', '==', companyId)
            );
            const snapshot = await getDocs(q);
            const packages = [];
            snapshot.forEach((doc) => {
                packages.push({ id: doc.id, ...doc.data() });
            });
            return packages;
        } catch (error) {
            console.error('Error fetching packages:', error);
            throw new Error('Failed to fetch packages');
        }
    }

    /**
     * Add a package
     */
    async addPackage(data, companyId = 'system') {
        const actorUid = await requireSuperAdminSettingsAccess();
        companyId = 'system';
        try {
            const packageData = {
                ...data,
                companyId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const docRef = await addDoc(collection(db, 'packages'), packageData);
            return { id: docRef.id, ...packageData };
        } catch (error) {
            console.error('Error adding package:', error);
            throw new Error('Failed to add package');
        }
    }

    /**
     * Update a package
     */
    async updatePackage(packageId, data) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            const docRef = doc(db, 'packages', packageId);
            await updateDoc(docRef, {
                ...data,
                updatedAt: serverTimestamp(),
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating package:', error);
            throw new Error('Failed to update package');
        }
    }

    /**
     * Delete a package
     */
    async deletePackage(packageId) {
        const actorUid = await requireSuperAdminSettingsAccess();
        try {
            await deleteDoc(doc(db, 'packages', packageId));
            return { success: true };
        } catch (error) {
            console.error('Error deleting package:', error);
            throw new Error('Failed to delete package');
        }
    }

    /**
     * Get all super admins
     */
    async getSuperAdmins() {
        try {
            await requireSuperAdminSettingsAccess();
            const [roleSnap, userRoleSnap] = await Promise.all([
                getDocs(query(collection(db, 'users'), where('role', 'in', ['super_admin', 'superadmin']))),
                getDocs(query(collection(db, 'users'), where('userRole', 'in', ['super_admin', 'superadmin']))),
            ]);
            const byId = new Map();
            [...roleSnap.docs, ...userRoleSnap.docs].forEach((row) => {
                byId.set(row.id, { id: row.id, ...row.data() });
            });
            return Array.from(byId.values()).sort((a, b) => String(a.displayName || a.email || '').localeCompare(String(b.displayName || b.email || '')));
        } catch (error) {
            console.error('Error fetching super admins:', error);
            throw error;
        }
    }

    /**
     * Add a super admin
     */
    async addSuperAdmin(data) {
        try {
            const { createPlatformAdmin } = await import('./platformIntegrationService');
            return await createPlatformAdmin(data);
        } catch (error) {
            console.error('Error adding super admin:', error);
            throw error;
        }
    }

    /**
     * Update a super admin
     */
    async updateSuperAdmin(adminId, data) {
        try {
            const actor = await requireSuperAdminSettingsAccess();
            const docRef = doc(db, 'users', adminId);
            const current = await getDoc(docRef);
            if (!current.exists() || !['super_admin', 'superadmin'].includes(normalizeAdminRole(current.data()?.role || current.data()?.userRole))) throw new Error('Super Admin account was not found.');
            if (adminId === actor && (data.isActive === false || data.isBlocked === true)) throw new Error('You cannot disable your own Super Admin account.');
            const { password, confirmPassword, ...safeData } = data || {};
            // Firebase Authentication passwords must never be stored in Firestore.
            // The UI may use a non-empty password field only as a signal to send
            // the administrator a secure reset link.
            await updateDoc(docRef, {
                ...safeData,
                role: 'super_admin',
                userRole: 'super_admin',
                updatedAt: serverTimestamp(),
            });
            return { success: true, passwordResetRequested: Boolean(password) };
        } catch (error) {
            console.error('Error updating super admin:', error);
            throw error;
        }
    }

    /**
     * Delete a super admin
     */
    async deleteSuperAdmin(adminId) {
        try {
            const actor = await requireSuperAdminSettingsAccess();
            if (adminId === actor) throw new Error('You cannot disable your own Super Admin account.');
            const ref = doc(db, 'users', adminId);
            const current = await getDoc(ref);
            if (!current.exists() || !['super_admin', 'superadmin'].includes(normalizeAdminRole(current.data()?.role || current.data()?.userRole))) throw new Error('Super Admin account was not found.');
            await updateDoc(ref, { isActive: false, isBlocked: true, disabledAt: serverTimestamp(), updatedAt: serverTimestamp() });
            return { success: true, disabled: true };
        } catch (error) {
            console.error('Error disabling super admin:', error);
            throw error;
        }
    }

    /**
     * Test provider connection
     */
    async testProviderConnection(provider, type) {
        try {
            const { testPlatformIntegration } = await import('./platformIntegrationService');
            return await testPlatformIntegration(type || provider);
        } catch (error) {
            console.error(`Error testing ${type || provider} connection:`, error);
            throw error;
        }
    }
}

export default new SettingsService();